/**
 * Material-UI styles for the VideoPlayer component.
 *
 * @module components/sim/takeSim/VideoPlayer
 * @author Snowfly
 * @version 1.0.0
 */

import { makeStyles } from '@material-ui/core/styles';

/**
 * Creates a set of JSS styles used for styling various components of the application.
 *
 * This function defines styles for several elements including dialogs, panels, buttons,
 * video players, and other UI components. Styles are applied using Material-UI's `makeStyles` API.
 *
 * @constant {Function} useStyles
 *
 * @property {Object} videoDialog
 * Configures the layout and styling for a full-screen video dialog, including overrides for
 * Material-UI dialog, backdrop, and paper components.
 *
 * @property {Object} panelParent
 * Defines styles for the parent container of a panel, enabling scrollable content and ensuring
 * proper layout dimensions.
 *
 * @property {Object} previewDialogTitle
 * Configures the styles for the title of the preview dialog, including text color and background.
 *
 * @property {Object} closeButton
 * Provides styling for a close button within dialogs, ensuring proper alignment and color.
 *
 * @property {Object} previewDialog
 * Configures the styling for a preview dialog component, including responsiveness, alignment, and
 * child components such as video wrappers and transform elements.
 *
 * @property {Object} previewPanel
 * Sets the style for a panel containing preview media, including layout and interaction styling for
 * nested transform wrapper and video elements.
 *
 * @property {Object} videoModal
 * Styles for a video modal component, including border and positional adjustments.
 *
 * @property {Object} success
 * Defines the styles for a success indicator, including font size, color, and spacing.
 *
 * @property {Object} videoPlayer
 * Specifies styles for a video player container, ensuring proper layout and responsiveness.
 *
 * @property {Object} zoomBar
 * Configures the styling of a zoom control bar, including position, background, and hover effects.
 *
 * @property {Object} dialogFooter
 * Provides styles for the footer of dialogs, including layout configurations and alignment for child elements.
 */
export const useStyles = makeStyles((/* theme */) => ({
    videoDialog: {
        width: '100%',
        height: '100%',
        left: '0',
        top: '0',
        margin: '0',
        padding: '0',

        '& .muiBackdrop-root': {
            backgroundColor: 'rgba(255, 200, 200, 0.4)',
        },
        '& .MuiDialog-container.MuiDialog-scrollPaper': {
            left: '0 !important',
            top: '0 !important',
            width: '100% !important',
            height: '100% !important',
            maxWidth: '100% !important',
            maxHeight: '100% !important',
        },
        '& .MuiDialog-paper': {
            maxWidth: 'unset !important',
            maxHeight: 'unset !important',
            width: '100% !important',
            height: '100% !important',
            padding: '0 !important',
            margin: '0 !important',
        },
    },
    panelParent: {
        overflow: 'auto',
        display: 'block',
        width: '100%',
        height: 'calc(100% - 4em)',
    },
    previewDialogTitle: {
        color: 'white',
        background: '#264173',
    },
    closeButton: {
        color: 'white',
        float: 'right',
        marginTop: -6,
    },
    previewDialog: {
        textAlign: 'center',
        margin: 0,
        position: 'relative',
        width: '100%',
        height: 'calc(100% - 4.25em)',
        overflow: 'hidden',
        cursor: 'grab',

        '& .react-transform-wrapper': {
            height: '100%',
            width: '100%',

            '& .react-transform-component': {
                height: '100%',
                width: '100%',
            },
        },

        video: {
            width: '100%',
            height: 'auto',
        }
    },
    previewPanel: {
        position: 'relative',
        width: '100%',
        height: 'auto',
        margin: '2em',
        overflow: 'visible',
        cursor: 'grab',
        display: 'block',

        '& .react-transform-wrapper': {
            height: 'calc(100% - 4em)',
            width: 'calc(100% - 4em)',
            overflow: 'visible',

            '& .react-transform-component': {
                height: '100%',
                width: '100%',
                overflow: 'visible',
            },
        },

        '& video': {
            position: 'relative',
            width: '100%',
            height: 'auto',
            display: 'block',
        },
    },
    videoModal: {
        border: '2px solid black',
        position: 'relative',
    },
    success: {
        fontSize: '1rem',
        color: '#2CB174',
        marginRight: '0.25em',
    },
    videoPlayer: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        padding: '0',
    },
    zoomBar: {
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        top: '1em',
        left: '1em',
        zIndex: '9999',
        isolation: 'isolate',
        backgroundColor: '#DEDEDE',
        border: '1px solid #33333388',
        borderRadius: '8px',
        boxShadow: '2px 2px 4px 0 #88888888',
        opacity: '0.4',

        '&:hover': {
            opacity: '1',
        }
    },
    dialogFooter: {
        padding: '0em',
        textAlign: 'left',
        height: '4em',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignContent: 'center',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '1rem',
    },
}));

export default useStyles;
